import { getScoreClass } from '@/utils/scoreUtils';
import { supabase } from '@/utils/supabase';
import { IonButton, IonButtons, IonContent, IonHeader, IonIcon, IonItem, IonLabel, IonList, IonTitle, IonToolbar } from '@ionic/react';
import { chevronBack } from 'ionicons/icons';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HoleData, PlayerInfo, ScoreData } from './types';
import { mediumImpact } from '@/utils/capacitorUtils';
import GolfIcon from '../GolfIcon';
import { useHistory } from 'react-router';
import HoleListSelector from './HoleListSelector';

interface ScoreOnlyViewProps {
  gameId: string;
  teeId: string;
  hasGps: boolean;
  par: number;
  players: PlayerInfo[];
  currentPlayer: PlayerInfo;
  holeData: HoleData[];
  activeHole: number;
  navigateToHole: (hole: number) => void;
  handleLeaveGame: () => void;
  onSkipHole: () => void;
  openScoreModal?: (playerId: string) => void;
  onSwitchToMapView?: () => void;
  initialScoreData?: Record<string, Record<number, ScoreData>>;
  clubName: string;
  courseName: string;
  gameType?: string;
  ownerId?: string; // ID of the user who created/owns the game
  pageRef?: any;
}

const ScoreOnlyView: React.FC<ScoreOnlyViewProps> = ({
  activeHole,
  holeData,
  players,
  currentPlayer,
  navigateToHole,
  handleLeaveGame,
  openScoreModal,
  onSwitchToMapView,
  gameId,
  hasGps,
  par,
  initialScoreData,
  clubName,
  courseName,
  gameType = 'strokeplay', // default to strokeplay if not provided
  ownerId = null, // ID of the user who created/owns the game
  pageRef
}) => {
  const { t } = useTranslation();
  const [playerScores, setPlayerScores] = useState<Record<string, Record<number, ScoreData>>>(initialScoreData || {});
  const [isHoleListOpen, setIsHoleListOpen] = useState<boolean>(false);
  const history = useHistory();

  // Set up Supabase realtime subscription for game scores
  useEffect(() => {
    if (!gameId) return;

    // Create a single channel for the entire game
    // This ensures all players are connected to the same channel
    const channelName = `game-scores-${gameId}`;

    // Subscribe to score updates
    const channel = supabase
      .channel(channelName)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'game_scores',
        filter: `game_id=eq.${gameId}`,
      }, (payload: any) => {
        // When we receive a score update
        if (payload.new) {
          const {
            player_id,
            hole_number,
            score,
            putts,
            penalties,
            water_penalties,
            ob_penalties,
            drop_penalties,
            fairway_hit
          } = payload.new as any;
          // Update scores for all holes, not just the current one
          // This ensures complete synchronization across all players
          setPlayerScores(prev => {
            const playerScoreMap = { ...prev };

            // Initialize player's scores if not exists
            if (!playerScoreMap[player_id]) {
              playerScoreMap[player_id] = {}; // Initialize empty object for this player
            }

            // Update the score for this specific hole
            playerScoreMap[player_id] = {
              ...playerScoreMap[player_id],
              [hole_number]: {
                score,
                putts: putts || 2,
                penalties: penalties || 0,
                waterPenalties: water_penalties || 0,
                obPenalties: ob_penalties || 0,
                dropPenalties: drop_penalties || 0,
                fairwayHit: fairway_hit || 'center'
              } as ScoreData
            };

            return playerScoreMap;
          });
        }
      })
      .subscribe();

    // Cleanup subscription on unmount
    return () => {
      // console.log(`SOV: Unsubscribing from Supabase channel: ${channelName}`);
      if (channel) {
        channel.unsubscribe();
      }
    };
  }, [gameId]); // Only depend on gameId, not activeHole to avoid recreating subscription

  // Get the score display for a player
  const getScoreDisplay = (playerId: string) => {
    const holeData = playerScores?.[playerId]?.[activeHole];
    return holeData?.score?.toString() || t('enter_score');
  };

  return (
    <>
      <IonHeader className="ion-no-border">
        <IonToolbar color="light">
          <IonButtons slot="start">
            <IonButton onClick={handleLeaveGame} fill="clear">
              <IonIcon slot="icon-only" icon={chevronBack} />
            </IonButton>
          </IonButtons>
          <IonButtons slot="end">
            {hasGps && (
              <IonButton shape="round" fill='outline' onClick={onSwitchToMapView}>
                <div className="text-xs font-medium">GPS</div>
              </IonButton>
            )}
            <IonButton shape="round" fill='clear' onClick={() => history.push(`/game/${gameId}/settings`)}>
              <div className="text-xs font-medium"><GolfIcon name="settings" /></div>
            </IonButton>
          </IonButtons>
          <IonTitle>
            <div className="flex flex-col items-center">
              <div className="text-sm font-medium truncate max-w-[80vw]">{clubName}</div>
              <div className="text-xs text-gray-500 truncate max-w-[80vw]">{courseName}</div>
            </div>
          </IonTitle>
        </IonToolbar>
        <IonToolbar color="light">
          <div className="px-2">
            <div className="flex justify-between items-center">
              <div className="text-sm font-medium">
                {t(gameType.toLowerCase())}
              </div>
              <div className="flex items-center space-x-2">
                <div onClick={() => setIsHoleListOpen(true)} className="bg-primary text-primary-foreground px-2 py-1 rounded-md text-xs">
                  {t('hole')} {activeHole}
                </div>
                <div className="bg-primary text-primary-foreground px-2 py-1 rounded-md text-xs">
                  PAR {par}
                </div>
              </div>
            </div>
          </div>
        </IonToolbar>
      </IonHeader>

      <IonContent scrollY={false}>
        <IonList>
          {players.map((player) => (
            <IonItem key={player.id} detail={false} lines="full">
              <div className="flex items-center w-full gap-2 py-2">
                <div className="flex-shrink-0 w-10 h-10 rounded-full overflow-hidden bg-gray-200">
                  {player.avatar ? (
                    <img src={player.avatar} alt={player.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-primary text-white font-bold text-lg">
                      {player.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <IonLabel>
                    <div className="flex items-center gap-1">
                      <h2 className="font-medium">{player.name}</h2>
                      {ownerId && player.userId === ownerId && (
                        <span title={t('game_owner')} className="text-primary">
                          <GolfIcon name="crown" themeAware={false} style={{ width: '16px', height: '16px' }} />
                        </span>
                      )}
                    </div>
                    {currentPlayer && player.id === currentPlayer.id && (
                      <p className="text-xs text-primary">{t('you')}</p>
                    )}
                  </IonLabel>
                </div>
                <IonButton fill="clear" className={`size-12 m-0 rounded-lg ${getScoreDisplay(player.userId) === t('enter_score') ? 'bg-input w-auto' : ''}`} onClick={(e) => {
                  e.stopPropagation();
                  mediumImpact();
                  openScoreModal?.(player.id);
                }}>
                  <span className={`font-medium text-lg px-2 ${getScoreDisplay(player.userId) === t('enter_score') ? 'text-sm text-foreground' : getScoreClass(parseInt(getScoreDisplay(player.userId)), par)}`}>{getScoreDisplay(player.userId)}</span>
                </IonButton>
              </div>
            </IonItem>
          ))}
        </IonList>
        {/* Hole List Selector Modal */}
        <HoleListSelector
          isOpen={isHoleListOpen}
          onClose={() => setIsHoleListOpen(false)}
          totalHoles={holeData.length}
          parentElement={pageRef}
          activeHole={activeHole}
          onSelectHole={(hole) => {
            navigateToHole(hole);
          }}
          holeData={holeData}
        />
      </IonContent>
    </>
  );
};

export default ScoreOnlyView;
